import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Search, Pencil, Copy, Trash2, BookMarked, Layers, Star, Settings2, Check,
} from "lucide-react";
import { api } from "../lib/api";
import { euro } from "../lib/format";
import { priceSuffix } from "../lib/units";
import UnitSelect from "../components/UnitSelect";
import { CategoryBadge, ITEM_TYPE_LABELS, ITEM_TYPE_BADGE } from "../components/CategoryBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const COLOR_SWATCHES = ["#4F46E5", "#DB2777", "#F59E0B", "#0891B2", "#16A34A", "#DC2626", "#7C3AED", "#0D9488", "#CA8A04", "#2563EB"];

const emptyItem = () => ({
  title: "", description: "", category: "", item_type: "base",
  unit_price: 0, unit: "corpo", quantity: 1, min_qty: 1, max_qty: 10,
  enabled_default: true, work_days: 0, discount_type: "none", discount_value: 0, iva_rate: 22,
});

const emptyPkg = (def = false) => ({
  id: crypto.randomUUID(), name: "", subtitle: "", features: [], price: 0,
  discount_type: "none", discount_value: 0, work_days: 0, iva_rate: 22,
  recommended: false, default_selected: def,
});
const emptyGroup = () => ({
  title: "", description: "", category: "", packages: [emptyPkg(true), emptyPkg(false)],
});

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function Catalog() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");

  const [itemDialog, setItemDialog] = useState(null); // {mode, data, id}
  const [groupDialog, setGroupDialog] = useState(null);
  const [catDialog, setCatDialog] = useState(false);

  const loadAll = async () => {
    const [c, i, g] = await Promise.all([
      api.get("/catalog/categories"),
      api.get("/catalog/items"),
      api.get("/catalog/groups"),
    ]);
    setCategories(c.data);
    setItems(i.data);
    setGroups(g.data);
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const filteredItems = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((it) => {
      if (catFilter !== "all" && it.category !== catFilter) return false;
      if (s && !(`${it.title} ${it.description}`.toLowerCase().includes(s))) return false;
      return true;
    });
  }, [items, search, catFilter]);

  const filteredGroups = useMemo(() => {
    const s = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (catFilter !== "all" && g.category !== catFilter) return false;
      if (s && !(`${g.title} ${g.description}`.toLowerCase().includes(s))) return false;
      return true;
    });
  }, [groups, search, catFilter]);

  const saveItem = async () => {
    const d = itemDialog.data;
    if (!d.title.trim()) return toast.error("Inserisci il titolo del servizio");
    const payload = {
      ...d,
      unit_price: Number(d.unit_price) || 0,
      work_days: Number(d.work_days) || 0,
      min_qty: Number(d.min_qty) || 1,
      max_qty: Number(d.max_qty) || 1,
      discount_value: Number(d.discount_value) || 0,
      iva_rate: Number(d.iva_rate) || 22,
    };
    try {
      if (itemDialog.id) await api.put(`/catalog/items/${itemDialog.id}`, payload);
      else await api.post("/catalog/items", payload);
      toast.success("Servizio salvato nel Listino!");
      setItemDialog(null);
      await loadAll();
    } catch (e) { toast.error("Errore nel salvataggio"); }
  };

  const saveGroup = async () => {
    const d = groupDialog.data;
    if (!d.title.trim()) return toast.error("Inserisci il titolo del gruppo");
    const payload = {
      ...d,
      packages: d.packages.map((p) => ({
        ...p,
        price: Number(p.price) || 0,
        discount_value: Number(p.discount_value) || 0,
        work_days: Number(p.work_days) || 0,
        iva_rate: Number(p.iva_rate) || 22,
        features: Array.isArray(p.features) ? p.features.filter(Boolean)
          : String(p.features || "").split("\n").map((x) => x.trim()).filter(Boolean),
      })),
    };
    try {
      if (groupDialog.id) await api.put(`/catalog/groups/${groupDialog.id}`, payload);
      else await api.post("/catalog/groups", payload);
      toast.success("Template Pacchetti salvato nel Listino!");
      setGroupDialog(null);
      await loadAll();
    } catch (e) { toast.error("Errore nel salvataggio"); }
  };

  const dupItem = async (id) => { await api.post(`/catalog/items/${id}/duplicate`); toast.success("Servizio duplicato"); await loadAll(); };
  const delItem = async (id) => { await api.delete(`/catalog/items/${id}`); toast.success("Servizio eliminato"); await loadAll(); };
  const dupGroup = async (id) => { await api.post(`/catalog/groups/${id}/duplicate`); toast.success("Template duplicato"); await loadAll(); };
  const delGroup = async (id) => { await api.delete(`/catalog/groups/${id}`); toast.success("Template eliminato"); await loadAll(); };

  if (loading)
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="catalog-page">
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <BookMarked style={{ width: 20, height: 20 }} className="text-primary" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">Listino Servizi</h1>
            <p className="text-sm text-slate-500">Salva voci, add-on e pacchetti riutilizzabili da importare nei preventivi.</p>
          </div>
        </div>
        <Button variant="outline" data-testid="manage-categories-button" onClick={() => setCatDialog(true)} className="h-10">
          <Settings2 style={{ width: 16, height: 16 }} className="mr-2" /> Gestisci Categorie
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="catalog-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca servizi o pacchetti…" className="pl-9" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="catalog-category-filter" className="w-full sm:w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="items">
        <TabsList className="mb-6">
          <TabsTrigger value="items" data-testid="catalog-tab-items">Voci Singole e Add-on</TabsTrigger>
          <TabsTrigger value="groups" data-testid="catalog-tab-groups">Template Pacchetti Tier</TabsTrigger>
        </TabsList>

        {/* TAB A */}
        <TabsContent value="items">
          <div className="flex justify-end mb-4">
            <Button data-testid="new-catalog-item-button" onClick={() => setItemDialog({ mode: "new", data: emptyItem(), id: null })}>
              <Plus style={{ width: 16, height: 16 }} className="mr-1.5" /> Nuovo Servizio a Listino
            </Button>
          </div>
          {filteredItems.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-16">Nessun servizio trovato.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((it) => (
                <div key={it.id} data-testid={`catalog-item-card-${it.id}`}
                  className="bg-card border border-slate-200/80 rounded-xl p-4 shadow-sm flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <CategoryBadge name={it.category} categories={categories} />
                    <Badge variant="outline" className={ITEM_TYPE_BADGE[it.item_type]}>{ITEM_TYPE_LABELS[it.item_type]}</Badge>
                  </div>
                  <h3 className="font-semibold text-slate-900 leading-snug">{it.title}</h3>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2 flex-1">{it.description || "—"}</p>
                  <div className="flex items-baseline gap-1 mt-3">
                    <span className="font-mono font-extrabold text-lg text-slate-900">{euro(it.unit_price)}</span>
                    <span className="text-xs text-slate-400">{priceSuffix(it.unit)}</span>
                    {it.work_days > 0 && <span className="text-xs text-slate-400 ml-auto">{it.work_days} gg consegna</span>}
                  </div>
                  <div className="flex items-center gap-1 mt-4 pt-3 border-t border-slate-100">
                    <Button variant="ghost" size="sm" data-testid={`catalog-item-edit-${it.id}`}
                      onClick={() => setItemDialog({ mode: "edit", data: { ...emptyItem(), ...it }, id: it.id })}
                      className="text-slate-600"><Pencil style={{ width: 14, height: 14 }} className="mr-1" /> Modifica</Button>
                    <Button variant="ghost" size="icon" data-testid={`catalog-item-duplicate-${it.id}`}
                      onClick={() => dupItem(it.id)} className="text-slate-500 h-8 w-8"><Copy style={{ width: 14, height: 14 }} /></Button>
                    <Button variant="ghost" size="icon" data-testid={`catalog-item-delete-${it.id}`}
                      onClick={() => delItem(it.id)} className="text-slate-400 hover:text-red-600 h-8 w-8 ml-auto"><Trash2 style={{ width: 14, height: 14 }} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TAB B */}
        <TabsContent value="groups">
          <div className="flex justify-end mb-4">
            <Button data-testid="new-catalog-group-button" onClick={() => setGroupDialog({ mode: "new", data: emptyGroup(), id: null })}>
              <Plus style={{ width: 16, height: 16 }} className="mr-1.5" /> Nuovo Template Pacchetti
            </Button>
          </div>
          {filteredGroups.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-16">Nessun template trovato.</p>
          ) : (
            <div className="space-y-4">
              {filteredGroups.map((g) => (
                <div key={g.id} data-testid={`catalog-group-card-${g.id}`}
                  className="bg-card border border-slate-200/80 rounded-xl p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2.5">
                      <Layers style={{ width: 18, height: 18 }} className="text-primary" />
                      <div>
                        <h3 className="font-semibold text-slate-900">{g.title}</h3>
                        <p className="text-sm text-slate-500">{g.description || "—"}</p>
                      </div>
                    </div>
                    <CategoryBadge name={g.category} categories={categories} />
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {g.packages.map((p) => (
                      <div key={p.id} className={`rounded-lg border p-3 ${p.recommended ? "border-primary ring-1 ring-primary/20" : "border-slate-200"}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm text-slate-900">{p.name}</span>
                          {p.recommended && <Star style={{ width: 13, height: 13 }} className="text-primary" fill="currentColor" />}
                        </div>
                        <p className="font-mono font-bold text-slate-900 mt-1">{euro(p.price)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 mt-4 pt-3 border-t border-slate-100">
                    <Button variant="ghost" size="sm" data-testid={`catalog-group-edit-${g.id}`}
                      onClick={() => setGroupDialog({ mode: "edit", data: { ...emptyGroup(), ...g }, id: g.id })}
                      className="text-slate-600"><Pencil style={{ width: 14, height: 14 }} className="mr-1" /> Modifica</Button>
                    <Button variant="ghost" size="icon" data-testid={`catalog-group-duplicate-${g.id}`}
                      onClick={() => dupGroup(g.id)} className="text-slate-500 h-8 w-8"><Copy style={{ width: 14, height: 14 }} /></Button>
                    <Button variant="ghost" size="icon" data-testid={`catalog-group-delete-${g.id}`}
                      onClick={() => delGroup(g.id)} className="text-slate-400 hover:text-red-600 h-8 w-8 ml-auto"><Trash2 style={{ width: 14, height: 14 }} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {itemDialog && (
        <ItemDialog state={itemDialog} setState={setItemDialog} categories={categories} onSave={saveItem} />
      )}
      {groupDialog && (
        <GroupDialog state={groupDialog} setState={setGroupDialog} categories={categories} onSave={saveGroup} />
      )}
      {catDialog && (
        <CategoryDialog open={catDialog} onClose={() => setCatDialog(false)} categories={categories} reload={loadAll} />
      )}
    </div>
  );
}

function ItemDialog({ state, setState, categories, onSave }) {
  const d = state.data;
  const upd = (patch) => setState((s) => ({ ...s, data: { ...s.data, ...patch } }));
  return (
    <Dialog open onOpenChange={() => setState(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="catalog-item-dialog">
        <DialogHeader>
          <DialogTitle>{state.id ? "Modifica Servizio" : "Nuovo Servizio a Listino"}</DialogTitle>
          <DialogDescription>Compila i dettagli della voce riutilizzabile.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Titolo del servizio">
            <Input data-testid="item-dialog-title" value={d.title} onChange={(e) => upd({ title: e.target.value })} placeholder="Es. Sviluppo Sito Web Vetrina" />
          </Field>
          <Field label="Descrizione dettagliata">
            <Textarea data-testid="item-dialog-description" value={d.description} onChange={(e) => upd({ description: e.target.value })} rows={2} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Categoria">
              <Select value={d.category || ""} onValueChange={(v) => upd({ category: v })}>
                <SelectTrigger data-testid="item-dialog-category"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Tipo predefinito">
              <Select value={d.item_type} onValueChange={(v) => upd({ item_type: v })}>
                <SelectTrigger data-testid="item-dialog-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="base">Obbligatoria (Base)</SelectItem>
                  <SelectItem value="optional">Opzionale (Add-on)</SelectItem>
                  <SelectItem value="variable">Quantità Variabile</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Prezzo (€)">
              <Input type="number" min="0" step="0.01" data-testid="item-dialog-price" value={d.unit_price} onChange={(e) => upd({ unit_price: e.target.value })} className="font-mono" />
            </Field>
            <Field label="Unità di misura">
              <UnitSelect value={d.unit} onChange={(v) => upd({ unit: v })} testid="item-dialog-unit" />
            </Field>
            <Field label="Giorni consegna">
              <Input type="number" min="0" data-testid="item-dialog-work-days" value={d.work_days} onChange={(e) => upd({ work_days: e.target.value })} className="font-mono" />
            </Field>
            <Field label="Aliquota IVA">
              <Select value={String(d.iva_rate ?? 22)} onValueChange={(v) => upd({ iva_rate: Number(v) })}>
                <SelectTrigger data-testid="item-dialog-iva"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="22">22%</SelectItem>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="4">4%</SelectItem>
                  <SelectItem value="0">0%</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Sconto riga predefinito">
              <Select value={d.discount_type} onValueChange={(v) => upd({ discount_type: v })}>
                <SelectTrigger data-testid="item-dialog-discount-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  <SelectItem value="percent">Percentuale %</SelectItem>
                  <SelectItem value="amount">Importo €</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={d.discount_type === "percent" ? "Sconto %" : "Sconto €"}>
              <Input type="number" min="0" step="0.01" data-testid="item-dialog-discount-value"
                value={d.discount_value} disabled={d.discount_type === "none"}
                onChange={(e) => upd({ discount_value: e.target.value })} className="font-mono disabled:opacity-50" />
            </Field>
          </div>
          {d.item_type === "variable" && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Q.tà minima">
                <Input type="number" min="1" data-testid="item-dialog-min-qty" value={d.min_qty} onChange={(e) => upd({ min_qty: e.target.value })} className="font-mono" />
              </Field>
              <Field label="Q.tà massima">
                <Input type="number" min="1" data-testid="item-dialog-max-qty" value={d.max_qty} onChange={(e) => upd({ max_qty: e.target.value })} className="font-mono" />
              </Field>
            </div>
          )}
          {d.item_type === "optional" && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={d.enabled_default !== false} onChange={(e) => upd({ enabled_default: e.target.checked })} className="rounded border-slate-300" />
              Attivo di default nella vista cliente
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setState(null)}>Annulla</Button>
          <Button data-testid="item-dialog-save" onClick={onSave}>Salva nel Listino</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupDialog({ state, setState, categories, onSave }) {
  const d = state.data;
  const upd = (patch) => setState((s) => ({ ...s, data: { ...s.data, ...patch } }));
  const updPkg = (pi, patch) => upd({ packages: d.packages.map((p, i) => (i === pi ? { ...p, ...patch } : p)) });
  const addPkg = () => { if (d.packages.length < 4) upd({ packages: [...d.packages, emptyPkg(false)] }); };
  const removePkg = (pi) => { if (d.packages.length > 2) upd({ packages: d.packages.filter((_, i) => i !== pi) }); };
  const setDefault = (pi) => upd({ packages: d.packages.map((p, i) => ({ ...p, default_selected: i === pi })) });
  return (
    <Dialog open onOpenChange={() => setState(null)}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="catalog-group-dialog">
        <DialogHeader>
          <DialogTitle>{state.id ? "Modifica Template Pacchetti" : "Nuovo Template Pacchetti Tier"}</DialogTitle>
          <DialogDescription>Definisci 2-4 opzioni mutuamente esclusive.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-[1fr_1fr_1fr] gap-4">
            <Field label="Titolo del gruppo">
              <Input data-testid="group-dialog-title" value={d.title} onChange={(e) => upd({ title: e.target.value })} placeholder="Es. Pacchetti Sviluppo Web" />
            </Field>
            <Field label="Descrizione">
              <Input data-testid="group-dialog-description" value={d.description} onChange={(e) => upd({ description: e.target.value })} />
            </Field>
            <Field label="Categoria">
              <Select value={d.category || ""} onValueChange={(v) => upd({ category: v })}>
                <SelectTrigger data-testid="group-dialog-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {d.packages.map((p, pi) => (
              <div key={p.id} data-testid={`group-dialog-pkg-${pi}`} className={`rounded-lg border p-3 space-y-2.5 ${p.recommended ? "border-primary ring-1 ring-primary/30" : "border-slate-200"}`}>
                <div className="flex items-center gap-2">
                  <Input value={p.name} onChange={(e) => updPkg(pi, { name: e.target.value })} placeholder="Nome pacchetto" className="font-semibold h-9" />
                  {d.packages.length > 2 && (
                    <button onClick={() => removePkg(pi)} className="text-slate-300 hover:text-red-600 shrink-0"><Trash2 style={{ width: 15, height: 15 }} /></button>
                  )}
                </div>
                <Input value={p.subtitle} onChange={(e) => updPkg(pi, { subtitle: e.target.value })} placeholder="Sottotitolo" className="h-8 text-sm" />
                <Textarea rows={3} value={Array.isArray(p.features) ? p.features.join("\n") : p.features || ""}
                  onChange={(e) => updPkg(pi, { features: e.target.value.split("\n") })} placeholder="Punti chiave (uno per riga)" className="text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Prezzo"><Input type="number" min="0" step="0.01" value={p.price} onChange={(e) => updPkg(pi, { price: e.target.value })} className="font-mono h-8" /></Field>
                  <Field label="Giorni"><Input type="number" min="0" value={p.work_days} onChange={(e) => updPkg(pi, { work_days: e.target.value })} className="font-mono h-8" /></Field>
                </div>
                <label className="flex items-center justify-between text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1"><Star style={{ width: 13, height: 13 }} /> Consigliato</span>
                  <Switch checked={!!p.recommended} onCheckedChange={(v) => updPkg(pi, { recommended: v })} />
                </label>
                <label className="flex items-center justify-between text-sm text-slate-600">
                  <span>Default</span>
                  <Switch checked={!!p.default_selected} onCheckedChange={(v) => { if (v) setDefault(pi); }} />
                </label>
              </div>
            ))}
          </div>
          {d.packages.length < 4 && (
            <Button variant="outline" size="sm" data-testid="group-dialog-add-pkg" onClick={addPkg}>
              <Plus style={{ width: 14, height: 14 }} className="mr-1.5" /> Aggiungi opzione
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setState(null)}>Annulla</Button>
          <Button data-testid="group-dialog-save" onClick={onSave}>Salva nel Listino</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ open, onClose, categories, reload }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState(COLOR_SWATCHES[0]);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) return toast.error("Inserisci il nome della categoria");
    setBusy(true);
    try { await api.post("/catalog/categories", { name: name.trim(), color }); setName(""); toast.success("Categoria aggiunta"); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  const updColor = async (c, col) => { await api.put(`/catalog/categories/${c.id}`, { name: c.name, color: col }); await reload(); };
  const del = async (c) => { await api.delete(`/catalog/categories/${c.id}`); toast.success("Categoria eliminata"); await reload(); };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="category-dialog">
        <DialogHeader>
          <DialogTitle>Gestisci Categorie</DialogTitle>
          <DialogDescription>Aggiungi, rinomina o elimina le categorie del listino.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {categories.map((c) => (
            <div key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center gap-2 border border-slate-200 rounded-lg p-2">
              <CategoryBadge name={c.name} categories={categories} className="flex-1 justify-start" />
              <div className="flex items-center gap-1">
                {COLOR_SWATCHES.slice(0, 6).map((col) => (
                  <button key={col} onClick={() => updColor(c, col)} className="w-4 h-4 rounded-full border border-white ring-1 ring-slate-200"
                    style={{ backgroundColor: col }} title={col}>{c.color === col ? <Check className="w-3 h-3 text-white mx-auto" /> : null}</button>
                ))}
                <Button variant="ghost" size="icon" data-testid={`category-delete-${c.id}`} onClick={() => del(c)} className="h-7 w-7 text-slate-400 hover:text-red-600"><Trash2 style={{ width: 13, height: 13 }} /></Button>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 pt-4 space-y-3">
          <Field label="Nuova categoria">
            <Input data-testid="new-category-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Video & Motion" />
          </Field>
          <div className="flex items-center gap-2">
            {COLOR_SWATCHES.map((col) => (
              <button key={col} data-testid={`new-category-color-${col}`} onClick={() => setColor(col)}
                className={`w-6 h-6 rounded-full ring-2 ${color === col ? "ring-slate-900" : "ring-transparent"}`} style={{ backgroundColor: col }} />
            ))}
          </div>
          <Button data-testid="add-category-button" onClick={add} disabled={busy} className="w-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus style={{ width: 16, height: 16 }} className="mr-1.5" />} Aggiungi Categoria
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
